import React from "react";
import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f8fafc", color: "#1e293b", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" }}>
      {/* Header */}
      <header style={{ backgroundColor: "#ffffff", borderBottom: "1px solid #e2e8f0", position: "sticky", top: 0, zIndex: 10 }}>
        <div style={{ maxWidth: "900px", margin: "0 auto", padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "22px", fontWeight: "700", color: "#2563eb", letterSpacing: "-0.5px" }}>Phloz</span>
            <span style={{ fontSize: "14px", color: "#64748b", borderLeft: "1px solid #cbd5e1", paddingLeft: "12px" }}>Privacy Policy</span>
          </div>
          <Link
            to="/login"
            style={{
              fontSize: "14px",
              fontWeight: "500",
              color: "#2563eb",
              textDecoration: "none",
              padding: "6px 14px",
              borderRadius: "6px",
              border: "1px solid #bfdbfe",
              backgroundColor: "#eff6ff"
            }}
          >
            Go to App
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main style={{ maxWidth: "900px", margin: "0 auto", padding: "40px 24px 80px" }}>
        <div style={{ backgroundColor: "#ffffff", borderRadius: "12px", border: "1px solid #e2e8f0", padding: "48px 40px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
          <h1 style={{ fontSize: "32px", fontWeight: "800", color: "#0f172a", marginBottom: "8px", letterSpacing: "-0.5px" }}>
            Phloz Privacy Policy
          </h1>
          <p style={{ color: "#64748b", fontSize: "14px", marginBottom: "32px" }}>
            <strong>Effective Date:</strong> January 1, 2026 | <strong>Last Updated:</strong> October 23, 2026
          </p>

          <section style={{ marginBottom: "32px", lineHeight: "1.7" }}>
            <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#0f172a", marginBottom: "12px" }}>
              1. Introduction
            </h2>
            <p style={{ marginBottom: "12px" }}>
              Welcome to <strong>Phloz</strong> ("we," "our," or "us"), operated by TBP Labs. Phloz provides intelligent communication, outreach management, and workflow automation services accessible via <code>https://app.phloz.io</code> and associated APIs.
            </p>
            <p>
              We are committed to maintaining the privacy, security, and integrity of the personal data you entrust to us. This Privacy Policy describes how we collect, use, store, share, and protect your information, with special emphasis on our handling of data received through third-party integrations, including Google APIs.
            </p>
          </section>

          <section style={{ marginBottom: "32px", lineHeight: "1.7" }}>
            <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#0f172a", marginBottom: "12px" }}>
              2. Information We Collect
            </h2>
            <p style={{ marginBottom: "12px" }}>
              We collect information to provide, maintain, and enhance our services:
            </p>
            <ul style={{ paddingLeft: "24px", marginBottom: "12px" }}>
              <li style={{ marginBottom: "8px" }}>
                <strong>Account Information:</strong> When you create an account, we collect your name, email address, password hash, organization name, and billing details.
              </li>
              <li style={{ marginBottom: "8px" }}>
                <strong>Google User Data (via OAuth):</strong> When you link your Google or Gmail account to Phloz via Google OAuth (specifically requesting the <code>https://www.googleapis.com/auth/gmail.modify</code> or Google Sign-In scopes), we access:
                <ul style={{ paddingLeft: "20px", marginTop: "6px" }}>
                  <li>Your Google email address and user profile identifier for authentication and mailbox configuration.</li>
                  <li>OAuth access and refresh tokens to communicate with Google APIs on your behalf.</li>
                  <li>Email headers, message bodies, thread identifiers, recipient lists, and message timestamps strictly for messages sent through Phloz or replies directly related to your campaigns.</li>
                  <li>Message labels and read/unread flags strictly to identify customer replies and update campaign sequence states.</li>
                </ul>
              </li>
              <li style={{ marginBottom: "8px" }}>
                <strong>Communication & Campaign Data:</strong> Email templates, outreach contact lists, campaign scheduling preferences, and recipient interaction metrics (such as delivery status, opens, and link clicks).
              </li>
              <li style={{ marginBottom: "8px" }}>
                <strong>Log & Diagnostic Data:</strong> IP addresses, browser user-agents, timestamps, and error logs collected automatically to ensure system stability, prevent spam abuse, and guarantee platform security.
              </li>
            </ul>
          </section>

          <section style={{ marginBottom: "32px", lineHeight: "1.7" }}>
            <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#0f172a", marginBottom: "12px" }}>
              3. How We Use Your Information
            </h2>
            <p style={{ marginBottom: "12px" }}>
              We use the collected information strictly for legitimate operational purposes:
            </p>
            <ul style={{ paddingLeft: "24px", marginBottom: "12px" }}>
              <li style={{ marginBottom: "8px" }}>To create and manage your Phloz account and authenticate your sessions.</li>
              <li style={{ marginBottom: "8px" }}>To send user-scheduled email sequences through your connected Google mailbox.</li>
              <li style={{ marginBottom: "8px" }}>To detect incoming replies to active outreach campaigns so subsequent automated follow-ups are immediately paused.</li>
              <li style={{ marginBottom: "8px" }}>To monitor mailbox health, bounce rates, and deliverability metrics.</li>
              <li style={{ marginBottom: "8px" }}>To provide technical support, respond to user inquiries, and prevent fraud or security breaches.</li>
            </ul>
          </section>

          {/* GOOGLE LIMITED USE SECTION */}
          <section style={{ marginBottom: "36px", padding: "24px", backgroundColor: "#f0fdf4", border: "1px solid #86efac", borderRadius: "8px", lineHeight: "1.7" }}>
            <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#166534", marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px" }}>
              <span>4. Google API Services User Data Policy & Limited Use Disclosure</span>
            </h2>
            <p style={{ fontWeight: "600", color: "#15803d", marginBottom: "12px" }}>
              Phloz's use and transfer to any other app of information received from Google APIs will adhere to the{" "}
              <a
                href="https://developers.google.com/terms/api-services-user-data-policy"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#15803d", textDecoration: "underline", fontWeight: "700" }}
              >
                Google API Services User Data Policy
              </a>
              , including the Limited Use requirements.
            </p>
            <div style={{ color: "#166534", fontSize: "14px" }}>
              <p style={{ marginBottom: "10px" }}>
                Specifically, regarding information received from Google APIs (such as Gmail messages, metadata, and user credentials):
              </p>
              <ul style={{ paddingLeft: "20px", marginBottom: "10px" }}>
                <li style={{ marginBottom: "6px" }}>
                  <strong>User-Facing Features Only:</strong> We only access, use, or store Google user data to provide user-facing features that are prominent in Phloz's user interface (sending outreach sequences and detecting thread replies).
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <strong>No Generalized AI/ML Model Training:</strong> We <strong>never</strong> use Google user data (including email content, subject lines, or recipient metadata) to train, retrain, fine-tune, or improve generalized or non-personalized artificial intelligence (AI) or machine learning (ML) models.
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <strong>No Advertising or Marketing:</strong> We <strong>never</strong> use or transfer Google user data to serve advertisements, including retargeted, personalized, or interest-based advertising.
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <strong>No Sale or Third-Party Transfer:</strong> We do not sell, rent, or transfer Google user data to any third parties, except as strictly required to deliver the core service, comply with applicable laws, or as part of a corporate merger or acquisition with explicit notice.
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <strong>Strict Human Access Limits:</strong> Phloz employees and contractors do not view or read your Google user data unless:
                  <ul style={{ paddingLeft: "18px", marginTop: "4px" }}>
                    <li>You have given explicit, affirmative consent to investigate a specific message for support purposes;</li>
                    <li>It is necessary for security purposes (such as investigating an exploit or bug);</li>
                    <li>It is required to comply with a valid legal obligation or court order; or</li>
                    <li>The data is aggregated and anonymized for internal system performance reporting.</li>
                  </ul>
                </li>
              </ul>
            </div>
          </section>

          <section style={{ marginBottom: "32px", lineHeight: "1.7" }}>
            <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#0f172a", marginBottom: "12px" }}>
              5. Data Sharing and Third-Party Subprocessors
            </h2>
            <p style={{ marginBottom: "12px" }}>
              We do not sell, trade, or rent your personal information to third parties. We share data only with trusted infrastructure providers (subprocessors) that adhere to rigorous security standards:
            </p>
            <ul style={{ paddingLeft: "24px", marginBottom: "12px" }}>
              <li style={{ marginBottom: "8px" }}>
                <strong>Cloud Infrastructure:</strong> Amazon Web Services (AWS) for hosting databases, servers, and encrypted storage in ISO 27001 / SOC 2 certified data centers.
              </li>
              <li style={{ marginBottom: "8px" }}>
                <strong>Legal & Regulatory Authorities:</strong> Only when compelled by subpoena, court order, or valid legal process, and to the extent required by law.
              </li>
            </ul>
          </section>

          <section style={{ marginBottom: "32px", lineHeight: "1.7" }}>
            <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#0f172a", marginBottom: "12px" }}>
              6. Security of Your Data
            </h2>
            <p style={{ marginBottom: "12px" }}>
              We apply comprehensive technical and administrative security controls to safeguard your data:
            </p>
            <ul style={{ paddingLeft: "24px", marginBottom: "12px" }}>
              <li style={{ marginBottom: "8px" }}>
                <strong>Encryption in Transit:</strong> All data transmitted between your browser, our servers, and Google APIs is encrypted using Transport Layer Security (TLS 1.2 or TLS 1.3) with modern cipher suites.
              </li>
              <li style={{ marginBottom: "8px" }}>
                <strong>Encryption at Rest:</strong> OAuth tokens, credentials, and persistent data are encrypted at rest using industry-standard AES-256 encryption.
              </li>
              <li style={{ marginBottom: "8px" }}>
                <strong>Access Control:</strong> Administrative access is strictly restricted by role-based access control (RBAC), multi-factor authentication (MFA), and audit logging.
              </li>
            </ul>
          </section>

          <section style={{ marginBottom: "32px", lineHeight: "1.7" }}>
            <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#0f172a", marginBottom: "12px" }}>
              7. Data Retention and Deletion
            </h2>
            <p style={{ marginBottom: "12px" }}>
              We retain personal data only for as long as necessary to fulfill the purposes outlined in this policy or as required by law:
            </p>
            <ul style={{ paddingLeft: "24px", marginBottom: "12px" }}>
              <li style={{ marginBottom: "8px" }}>
                <strong>Connected Mailboxes:</strong> We retain OAuth credentials and sync logs only while your mailbox remains connected. If you disconnect your mailbox in Phloz Settings, the stored credentials and sync tokens are immediately and permanently removed.
              </li>
              <li style={{ marginBottom: "8px" }}>
                <strong>Revocation via Google:</strong> You can revoke Phloz's access to your Google account at any time by visiting{" "}
                <a
                  href="https://myaccount.google.com/permissions"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#2563eb", textDecoration: "underline" }}
                >
                  Google Account Security Permissions
                </a>
                .
              </li>
              <li style={{ marginBottom: "8px" }}>
                <strong>Data Deletion Requests:</strong> You may request complete erasure of your account, email logs, and associated personal information by emailing us at{" "}
                <a href="mailto:support@phloz.io" style={{ color: "#2563eb", textDecoration: "underline" }}>
                  support@phloz.io
                </a>
                . We will fulfill verified requests within 30 days.
              </li>
            </ul>
          </section>

          <section style={{ marginBottom: "32px", lineHeight: "1.7" }}>
            <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#0f172a", marginBottom: "12px" }}>
              8. Your Rights & Choices
            </h2>
            <p style={{ marginBottom: "12px" }}>
              Depending on your jurisdiction (including the EU General Data Protection Regulation and the California Consumer Privacy Act), you hold specific rights regarding your personal data:
            </p>
            <ul style={{ paddingLeft: "24px", marginBottom: "12px" }}>
              <li style={{ marginBottom: "8px" }}>The right to access, inspect, and receive a copy of your personal data.</li>
              <li style={{ marginBottom: "8px" }}>The right to rectify inaccurate or incomplete records.</li>
              <li style={{ marginBottom: "8px" }}>The right to request erasure ("right to be forgotten") of your personal data.</li>
              <li style={{ marginBottom: "8px" }}>The right to restrict or object to the processing of your data.</li>
              <li style={{ marginBottom: "8px" }}>The right to data portability.</li>
            </ul>
          </section>

          <section style={{ marginBottom: "32px", lineHeight: "1.7" }}>
            <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#0f172a", marginBottom: "12px" }}>
              9. Changes to This Privacy Policy
            </h2>
            <p style={{ marginBottom: "12px" }}>
              We may update this Privacy Policy from time to time to reflect modifications to our platform, technologies, or legal obligations. When updates occur, we will revise the "Last Updated" date at the top of this page. Significant changes will be communicated via in-app banner or email notice.
            </p>
          </section>

          <section style={{ lineHeight: "1.7" }}>
            <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#0f172a", marginBottom: "12px" }}>
              10. Contact Us
            </h2>
            <p style={{ marginBottom: "12px" }}>
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data handling practices, please contact our Data Protection Team at:
            </p>
            <div style={{ backgroundColor: "#f1f5f9", padding: "16px 20px", borderRadius: "8px", fontSize: "14px" }}>
              <p style={{ margin: "4px 0" }}><strong>Phloz / TBP Labs</strong></p>
              <p style={{ margin: "4px 0" }}><strong>Email:</strong> <a href="mailto:support@phloz.io" style={{ color: "#2563eb", textDecoration: "none" }}>support@phloz.io</a> / <a href="mailto:lakshya@tbplabs.tech" style={{ color: "#2563eb", textDecoration: "none" }}>lakshya@tbplabs.tech</a></p>
              <p style={{ margin: "4px 0" }}><strong>Website:</strong> <a href="https://app.phloz.io" style={{ color: "#2563eb", textDecoration: "none" }}>https://app.phloz.io</a></p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer style={{ backgroundColor: "#ffffff", borderTop: "1px solid #e2e8f0", padding: "24px", textAlign: "center", fontSize: "13px", color: "#64748b" }}>
        <p>© 2026 Phloz by TBP Labs. All rights reserved. | <Link to="/terms" style={{ color: "#64748b", textDecoration: "underline" }}>Terms of Service</Link></p>
      </footer>
    </div>
  );
}

